import { useEffect, useState } from "react";
import { usePublicResource } from "@/public/usePublicEvent";
import type { PublicHeat, PublicSummary } from "@/public/types";

function DemoSkeleton() {
  return (
    <div className="sc-demo-content" role="status" aria-label="Loading demo results">
      <span className="sc-tag">Read-only demo</span>
      <h2>Club day · Heat 1</h2>
      <p>Loading the published competition…</p>
      {[0, 1, 2].map((row) => (
        <div className="sc-skeleton" key={row} aria-hidden="true" />
      ))}
    </div>
  );
}

/** The SSR shell never opens a socket; hydration uses the shared public client. */
export function DemoResults({ compact = false }: { compact?: boolean }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? <LiveDemo compact={compact} /> : <DemoSkeleton />;
}

function LiveDemo({ compact }: { compact: boolean }) {
  const event = usePublicResource<PublicSummary>("/api/public/demo", "/api/public/demo/ws");
  const published = event.data?.published ? event.data : null;
  const heat = published?.heats[0];
  const scores = usePublicResource<PublicHeat>(heat ? `/api/public/demo/heats/${heat.id}` : null);
  const offline = !event.online;
  const error = event.error || scores.error;
  const loading = event.loading || (heat && scores.loading);
  const competitors = scores.data?.competitors;
  return (
    <div className={`sc-demo-content ${compact ? "compact" : ""}`}>
      <span className="sc-tag">Read-only demo</span>
      <h2>{heat?.name ?? "Club day · Heat 1"}</h2>
      <p>
        Fictional riders. An illustrative score recording, replayed through the public results
        service.
      </p>
      {offline && (
        <div className="sc-demo-alert" role="status">
          You are offline.{" "}
          {competitors ? "Showing the last loaded results." : "Connect to load demo results."}
        </div>
      )}
      {error && !offline && (
        <div className="sc-demo-alert" role="alert">
          {competitors
            ? "Updates are unavailable. Showing the last loaded results."
            : "Demo unavailable. Please try again."}{" "}
          <button
            className="sc-button sc-button-small"
            onClick={() => {
              event.refresh();
              scores.refresh();
            }}
          >
            Retry
          </button>
        </div>
      )}
      {loading && !competitors ? (
        <div role="status" aria-label="Loading demo results">
          {[0, 1, 2].map((row) => (
            <div className="sc-skeleton" key={row} aria-hidden="true" />
          ))}
        </div>
      ) : competitors?.length ? (
        <table className="sc-demo-table" aria-label="Demo heat results">
          <thead>
            <tr>
              <th scope="col">Place</th>
              <th scope="col">Rider</th>
              <th scope="col">Score</th>
            </tr>
          </thead>
          <tbody aria-live="polite" aria-relevant="text">
            {competitors.map((rider) => (
              <tr key={rider.heat_rider_id}>
                <td>{rider.placement ?? "—"}</td>
                <td>{rider.name}</td>
                <td>{rider.final ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        !error &&
        !offline && (
          <div className="sc-demo-empty">
            <h3>Demo is getting ready</h3>
            <p>The next published scores will appear here.</p>
            <button
              className="sc-button"
              onClick={() => {
                event.refresh();
                scores.refresh();
              }}
            >
              Refresh results
            </button>
          </div>
        )
      )}
      <p className="sc-demo-status" role="status">
        {offline
          ? "Offline"
          : error
            ? "Updates paused"
            : event.connection === "live"
              ? "Public results · updates pushed live"
              : "Public results · refreshing automatically"}
        {scores.updatedAt
          ? ` · Checked ${new Date(scores.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
          : ""}
      </p>
    </div>
  );
}
